import {
    DeleteOutlined,
    DislikeOutlined,
    LikeOutlined,
    MessageOutlined,
    PlusOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { AssistantPanelViewModel } from "@core/presentacion/component/assistant/assistant-panel.viewmodel";
import { IncPopconfirm } from "@core/presentacion/component/feedback/popconfirm/inc-popconfirm.component";
import { useViewModel } from "@core/presentacion/hook/use-view-model/use-view-model.hook";
import { container } from "@di/inversify.config";
import {
    Alert,
    Button,
    Drawer,
    Empty,
    Input,
    List,
    Select,
    Space,
    Spin,
    Tooltip,
    Typography,
} from "antd";
import { observer } from "mobx-react";
import {
    CSSProperties,
    FC,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

type AssistantPanelProps = {
    open: boolean;
    onClose: () => void;
};

const { Paragraph, Text, Title } = Typography;

const buildScreenContext = (pathname: string): Record<string, unknown> => {
    const parts = pathname.split("/").filter(Boolean);

    return {
        route: pathname,
        module: parts[0] ?? "home",
        section: parts[1] ?? null,
    };
};

const TYPING_DOT_DELAYS = ["0s", "0.15s", "0.3s"];

const typingDotStyle: CSSProperties = {
    animationDuration: "1s",
};

const _AssistantPanel: FC<AssistantPanelProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const viewModel = useViewModel(() =>
        container.get(AssistantPanelViewModel),
    );
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);

    const screenContext = useMemo(
        () => buildScreenContext(location.pathname),
        [location.pathname],
    );
    const suggestions = useMemo(
        () => [
            t("component:assistant.suggestions.tasks"),
            t("component:assistant.suggestions.indicators"),
            t("component:assistant.suggestions.dashboard"),
        ],
        [t],
    );
    const historyOptions = useMemo(
        () =>
            viewModel.conversations.map((conversation) => ({
                value: conversation.id,
                label: conversation.title || `#${conversation.id}`,
            })),
        [viewModel.conversations],
    );

    const handleSend = useCallback(async (): Promise<void> => {
        await viewModel.sendMessage(screenContext);
    }, [screenContext, viewModel]);

    const handleStartConversation = useCallback(async (): Promise<void> => {
        viewModel.setDraft("");
        await viewModel.startConversation(screenContext);
    }, [screenContext, viewModel]);

    const handleKeyDown = useCallback(
        async (event: KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
            if (event.key !== "Enter" || event.shiftKey) {
                return;
            }

            event.preventDefault();
            await handleSend();
        },
        [handleSend],
    );

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container === null) {
            return;
        }

        container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
        });
    }, [open, viewModel.activeConversation?.messages.length]);

    return (
        <Drawer
            title={t("component:assistant.title")}
            placement={"right"}
            width={460}
            open={open}
            onClose={onClose}
            destroyOnClose={false}
        >
            <div className={"tw-flex tw-h-full tw-flex-col tw-gap-4"}>
                <div
                    className={
                        "tw-flex tw-items-start tw-justify-between tw-gap-4"
                    }
                >
                    <div>
                        <Title
                            level={4}
                            className={"!tw-mb-1"}
                        >
                            {t("component:assistant.title")}
                        </Title>
                        <Paragraph className={"!tw-mb-0 tw-text-sm"}>
                            {t("component:assistant.subtitle")}
                        </Paragraph>
                    </div>
                    <Button
                        type={"primary"}
                        icon={<PlusOutlined />}
                        onClick={handleStartConversation}
                    >
                        {t("component:assistant.newConversation")}
                    </Button>
                </div>

                {viewModel.error ? (
                    <Alert
                        type={"error"}
                        message={t("component:assistant.loadError")}
                        showIcon
                    />
                ) : null}

                <div>
                    <Text strong>{t("component:assistant.history")}</Text>
                    <div
                        className={
                            "tw-mt-2 tw-flex tw-min-w-0 tw-items-center tw-gap-2"
                        }
                    >
                        {viewModel.conversations.length === 0 ? (
                            <Text type={"secondary"}>
                                {t("component:assistant.emptyHistory")}
                            </Text>
                        ) : (
                            <>
                                <div className={"tw-min-w-0 tw-w-0 tw-flex-1"}>
                                    <Select<number>
                                        className={"tw-w-full tw-min-w-0"}
                                        placeholder={t(
                                            "component:assistant.historyPlaceholder",
                                        )}
                                        value={
                                            viewModel.activeConversation?.id ??
                                            null
                                        }
                                        options={historyOptions}
                                        showSearch
                                        optionFilterProp={"label"}
                                        onChange={async (
                                            value: number,
                                        ): Promise<void> => {
                                            await viewModel.openConversation(
                                                value,
                                            );
                                        }}
                                    />
                                </div>
                                <IncPopconfirm
                                    title={t(
                                        "component:assistant.deleteConversationConfirm",
                                    )}
                                    disabled={
                                        viewModel.activeConversation === null
                                    }
                                    onConfirm={async (): Promise<void> => {
                                        if (
                                            viewModel.activeConversation ===
                                            null
                                        ) {
                                            return;
                                        }

                                        await viewModel.deleteConversation(
                                            viewModel.activeConversation.id,
                                        );
                                    }}
                                >
                                    <Tooltip
                                        title={t(
                                            "component:assistant.deleteConversation",
                                        )}
                                    >
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            loading={
                                                viewModel.activeConversation !==
                                                    null &&
                                                viewModel.isDeletingConversation(
                                                    viewModel.activeConversation
                                                        .id,
                                                )
                                            }
                                            disabled={
                                                viewModel.activeConversation ===
                                                null
                                            }
                                        />
                                    </Tooltip>
                                </IncPopconfirm>
                            </>
                        )}
                    </div>
                </div>

                <div className={"tw-rounded-2xl tw-bg-slate-50 tw-p-4"}>
                    <Text strong>
                        {t("component:assistant.suggestionsTitle")}
                    </Text>
                    <div className={"tw-mt-3 tw-flex tw-flex-wrap tw-gap-2"}>
                        {suggestions.map((suggestion) => (
                            <Button
                                key={suggestion}
                                icon={<MessageOutlined />}
                                onClick={async (): Promise<void> => {
                                    await viewModel.sendMessage(
                                        screenContext,
                                        suggestion,
                                    );
                                }}
                            >
                                {suggestion}
                            </Button>
                        ))}
                    </div>
                </div>

                <div
                    ref={messagesContainerRef}
                    className={
                        "tw-flex-1 tw-overflow-y-auto tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-p-4"
                    }
                >
                    {viewModel.loading ? (
                        <div
                            className={
                                "tw-flex tw-h-full tw-items-center tw-justify-center"
                            }
                        >
                            <Spin />
                        </div>
                    ) : (viewModel.activeConversation?.messages.length ?? 0) ===
                      0 ? (
                        <div
                            className={
                                "tw-flex tw-h-full tw-items-center tw-justify-center"
                            }
                        >
                            <Empty
                                description={t(
                                    "component:assistant.emptyState",
                                )}
                            />
                        </div>
                    ) : (
                        <List
                            dataSource={
                                viewModel.activeConversation?.messages ?? []
                            }
                            renderItem={(message) => {
                                const alignmentClass = message.isUser
                                    ? "tw-justify-end"
                                    : "tw-justify-start";
                                const bubbleClass = message.isUser
                                    ? "tw-bg-cyan-500 tw-text-white"
                                    : "tw-bg-slate-100 tw-text-slate-800";
                                const messageTextClass = message.isUser
                                    ? "!tw-mb-2 tw-whitespace-pre-line !tw-text-white"
                                    : "!tw-mb-2 tw-whitespace-pre-line !tw-text-slate-800";
                                const usefulType =
                                    message.feedback === "useful"
                                        ? "primary"
                                        : "text";
                                const notUsefulType =
                                    message.feedback === "not_useful"
                                        ? "primary"
                                        : "text";

                                return (
                                    <List.Item
                                        className={
                                            "!tw-border-0 !tw-px-0 !tw-py-2"
                                        }
                                    >
                                        <div
                                            className={`tw-flex tw-w-full ${alignmentClass}`}
                                        >
                                            <div
                                                className={`tw-max-w-[88%] tw-rounded-2xl tw-px-4 tw-py-3 ${bubbleClass}`}
                                            >
                                                {message.isPending ? (
                                                    <div
                                                        className={
                                                            "tw-flex tw-min-h-[28px] tw-items-center tw-gap-1 tw-py-1"
                                                        }
                                                    >
                                                        {TYPING_DOT_DELAYS.map(
                                                            (delay, index) => (
                                                                <span
                                                                    key={`${message.id}-${index}`}
                                                                    className={
                                                                        "tw-inline-block tw-h-2 tw-w-2 tw-rounded-full tw-bg-slate-400 tw-animate-bounce"
                                                                    }
                                                                    style={{
                                                                        ...typingDotStyle,
                                                                        animationDelay:
                                                                            delay,
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Paragraph
                                                        className={
                                                            messageTextClass
                                                        }
                                                    >
                                                        {message.content}
                                                    </Paragraph>
                                                )}

                                                {message.isAssistant &&
                                                !message.isPending ? (
                                                    <Space>
                                                        <Button
                                                            type={usefulType}
                                                            size={"small"}
                                                            icon={
                                                                <LikeOutlined />
                                                            }
                                                            loading={viewModel.isFeedbackLoading(
                                                                message.id,
                                                            )}
                                                            onClick={async (): Promise<void> => {
                                                                await viewModel.submitFeedback(
                                                                    message.id,
                                                                    {
                                                                        feedback:
                                                                            "useful",
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            {t(
                                                                "component:assistant.feedbackUseful",
                                                            )}
                                                        </Button>
                                                        <Button
                                                            type={notUsefulType}
                                                            size={"small"}
                                                            icon={
                                                                <DislikeOutlined />
                                                            }
                                                            loading={viewModel.isFeedbackLoading(
                                                                message.id,
                                                            )}
                                                            onClick={async (): Promise<void> => {
                                                                await viewModel.submitFeedback(
                                                                    message.id,
                                                                    {
                                                                        feedback:
                                                                            "not_useful",
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            {t(
                                                                "component:assistant.feedbackNotUseful",
                                                            )}
                                                        </Button>
                                                    </Space>
                                                ) : null}
                                            </div>
                                        </div>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </div>

                <div
                    className={
                        "tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-p-4"
                    }
                >
                    <Input.TextArea
                        value={viewModel.draft}
                        onChange={(event): void =>
                            viewModel.setDraft(event.target.value)
                        }
                        onKeyDown={handleKeyDown}
                        placeholder={t("component:assistant.inputPlaceholder")}
                        autoSize={{ minRows: 3, maxRows: 6 }}
                    />

                    <div
                        className={
                            "tw-mt-3 tw-flex tw-items-center tw-justify-between"
                        }
                    >
                        <Text type={"secondary"}>
                            {viewModel.sending
                                ? t("component:assistant.sending")
                                : location.pathname}
                        </Text>
                        <Button
                            type={"primary"}
                            icon={<SendOutlined />}
                            onClick={handleSend}
                            loading={viewModel.sending}
                        >
                            {t("component:assistant.send")}
                        </Button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export const AssistantPanel = observer(_AssistantPanel);
